import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '../../lib/format.js';
import { AnalyticsPanel } from './analytics-panel.js';
import { ChangeFigure, CompactMoney } from './figures.js';

/**
 * One headline figure — "₹1.8L", "42" — with how it moved since last month underneath.
 *
 * The figure is the biggest thing in the card on purpose: it is what the owner came for, and on
 * a phone held at arm's length a 30px number is readable where a 14px one is a smudge. Money is
 * written short in lakh and crore (see `formatCurrencyCompact`); the exact sum stays in the card
 * for the pointer and the screen reader.
 *
 * `change` is a ratio against the same days of last month, `null` when last month had nothing
 * to compare with, and left out entirely for a figure that has no comparison (the six-month
 * total) — in which case `caption` says what the figure covers instead.
 */
export function BigNumberCard({
  label,
  value,
  change,
  caption,
}: {
  label: string;
  value: { paise: number } | { count: number };
  change?: number | null;
  caption?: string;
}): ReactElement {
  const { t } = useTranslation();

  return (
    <AnalyticsPanel title={label} className="gap-1.5">
      <p className="text-3xl font-semibold tracking-tight tabular-nums" data-testid="big-number">
        {'paise' in value ? <CompactMoney paise={value.paise} /> : formatNumber(value.count)}
      </p>
      {change !== undefined ? (
        <p className="flex flex-wrap items-center gap-x-1.5 text-xs">
          {change === null ? (
            <span className="text-muted-foreground">{t('dashboard.analytics.noComparison')}</span>
          ) : (
            <>
              <ChangeFigure change={change} />
              <span className="text-muted-foreground">{t('dashboard.analytics.vsLastMonth')}</span>
            </>
          )}
        </p>
      ) : null}
      {caption ? <p className="text-xs text-muted-foreground">{caption}</p> : null}
    </AnalyticsPanel>
  );
}
